// RigSentinel — Daily Operations Module Foundation (Phase RCE-2)
//
// STORAGE LAYER
// -------------
// Single namespaced store for every Daily Operations kind:
//   rsc.dailyOps.v1
//
// Records are kind-tagged. The legacy `rsc.ddr.drafts.v1` namespace is
// PRESERVED — DDR continues to read/write its legacy draft for backward
// compatibility, and the new dailyOps store layers on top for any document
// that has consumed RCE context (so the RCE-1.0 audit trail is preserved
// even if the user reopens a legacy draft).

import {
  emptyDailyOpsDocument,
  validateDailyOpsDocument,
  freezeContextSnapshot,
} from "./schema";
import { isKindEnabled } from "./kinds";
import { scoped } from "@/lib/accountScope";

export const DAILY_OPS_STORAGE_KEY = "rsc.dailyOps.v1";

const _now   = () => new Date().toISOString();
const _genId = (kind) =>
  `dops_${kind}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
const _safeParse = (s, f) => {
  try { return JSON.parse(s) ?? f; } catch { return f; }
};

function _readAll() {
  return _safeParse(scoped.getItem(DAILY_OPS_STORAGE_KEY), []);
}
function _writeAll(rows) {
  scoped.setItem(DAILY_OPS_STORAGE_KEY, JSON.stringify(rows));
}

function _validateOrThrow(rec) {
  const v = validateDailyOpsDocument(rec);
  if (!v.ok) {
    const e = new Error("Daily Operations document failed validation.");
    e.validationErrors = v.errors;
    throw e;
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Create a new Daily Operations document. If `ctx` is provided the document
 * is born with a frozen contextSnapshot (the typical case — the editor
 * passes the live engineering-session context).
 */
export function createDailyOpsDocument(kind, payloadPatch = {}, ctx = null) {
  if (!isKindEnabled(kind))
    throw new Error(`Kind "${kind}" is registered but not enabled in this phase.`);
  let base = emptyDailyOpsDocument(kind);
  base = {
    ...base,
    payload: { ...base.payload, ...(payloadPatch || {}) },
    id: _genId(kind),
    createdAt: _now(),
    updatedAt: _now(),
    wellId:        ctx?.wellId        || "",
    projectId:     ctx?.projectId     || "",
    rigId:         ctx?.rigId         || "",
    bhaId:         ctx?.bhaId         || "",
    drillStringId: ctx?.drillStringId || "",
  };
  if (ctx) base = freezeContextSnapshot(base, ctx);
  _validateOrThrow(base);
  const all = _readAll();
  all.push(base);
  _writeAll(all);
  return base;
}

export function getDailyOpsDocument(id) {
  return _readAll().find((r) => r.id === id) || null;
}

export function listDailyOpsDocuments({ kind, wellId, includeInactive = false } = {}) {
  let rows = _readAll();
  if (kind)   rows = rows.filter((r) => r.kind === kind);
  if (wellId) rows = rows.filter((r) => r.wellId === wellId);
  if (!includeInactive) rows = rows.filter((r) => r.isActive !== false);
  return rows
    .slice()
    .sort((a, b) => String(b.updatedAt || "").localeCompare(String(a.updatedAt || "")));
}

/**
 * Patch a document. `patch.payload` is shallow-merged onto the existing
 * payload. The contextSnapshot is NEVER refreshed automatically — the
 * caller must invoke `rePullContextSnapshot` for that.
 */
export function updateDailyOpsDocument(id, patch) {
  const all = _readAll();
  const i = all.findIndex((r) => r.id === id);
  if (i < 0) throw new Error(`Daily Ops document ${id} not found.`);
  const prev = all[i];
  const next = {
    ...prev,
    ...patch,
    id: prev.id,
    kind: prev.kind,
    createdAt: prev.createdAt,
    contextSnapshot: prev.contextSnapshot,  // NEVER auto-clobber
    contextVersion: prev.contextVersion,
    payload: { ...prev.payload, ...(patch?.payload || {}) },
  };
  next.updatedAt = _now();
  _validateOrThrow(next);
  all[i] = next;
  _writeAll(all);
  return next;
}

/**
 * Explicit context refresh — the only way to update a frozen snapshot.
 * Returns the updated record. Refuses to refresh `final` / `archived`
 * documents (audit-trail invariant).
 */
export function rePullContextSnapshot(id, ctx) {
  const all = _readAll();
  const i = all.findIndex((r) => r.id === id);
  if (i < 0) throw new Error(`Daily Ops document ${id} not found.`);
  const prev = all[i];
  if (prev.status === "final" || prev.status === "archived")
    throw new Error(`Cannot re-pull context: document is ${prev.status}.`);
  const refreshed = freezeContextSnapshot(prev, ctx || {});
  refreshed.updatedAt = _now();
  _validateOrThrow(refreshed);
  all[i] = refreshed;
  _writeAll(all);
  return refreshed;
}

export function deleteDailyOpsDocument(id) {
  const all = _readAll();
  const next = all.filter((r) => r.id !== id);
  _writeAll(next);
  return all.length - next.length;
}

/** Test helper — never called from app code. */
export function _clearAllDailyOps() {
  scoped.removeItem(DAILY_OPS_STORAGE_KEY);
}
